import { Injectable } from '@angular/core';
import mapboxgl from 'mapbox-gl';
import { ILocalidad } from 'modelosSeguridad/src';

/**
 * LocalidadesSeguridadLayerManager
 *
 * Renderiza localidades de seguridad (polígonos) en el mapa.
 */
@Injectable({
  providedIn: 'root',
})
export class LocalidadesSeguridadLayerManager {
  private map: mapboxgl.Map | null = null;
  private sourceId = 'localidades-seguridad-source';
  private fillLayerId = 'localidades-seguridad-fill-layer';
  private outlineLayerId = 'localidades-seguridad-outline-layer';
  private isInitialized = false;

  private readonly defaultFill = '#009688';
  private readonly defaultOutline = '#004D40';

  private pendingFeatures: GeoJSON.Feature<GeoJSON.Polygon>[] | null = null;

  initialize(map: mapboxgl.Map): void {
    if (this.isInitialized) return;
    this.map = map;
    this.createSourceAndLayers();
    this.isInitialized = true;
  }

  cleanup(): void {
    if (!this.map) return;
    try {
      if (this.map.getLayer(this.outlineLayerId)) {
        this.map.removeLayer(this.outlineLayerId);
      }
      if (this.map.getLayer(this.fillLayerId)) {
        this.map.removeLayer(this.fillLayerId);
      }
      if (this.map.getSource(this.sourceId)) {
        this.map.removeSource(this.sourceId);
      }
    } catch {
      // map.style ya disposed
    }
    this.map = null;
    this.isInitialized = false;
  }

  private createSourceAndLayers(): void {
    if (!this.map) return;

    if (!this.map.getSource(this.sourceId)) {
      this.map.addSource(this.sourceId, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
        buffer: 128,
        tolerance: 0.375,
      });
    }

    if (!this.map.getLayer(this.fillLayerId)) {
      this.map.addLayer({
        id: this.fillLayerId,
        type: 'fill',
        source: this.sourceId,
        paint: {
          'fill-color': this.defaultFill,
          'fill-opacity': 0.14,
        },
      });
    }

    if (!this.map.getLayer(this.outlineLayerId)) {
      this.map.addLayer({
        id: this.outlineLayerId,
        type: 'line',
        source: this.sourceId,
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': this.defaultOutline,
          'line-width': 1,
          'line-opacity': 0.45,
          'line-dasharray': [2, 2],
        },
      });
    }

    this.moveBelowEntityLayers();
  }

  private moveBelowEntityLayers(): void {
    if (!this.map) return;
    try {
      const allLayers = this.map.getStyle()?.layers || [];
      const entityPrefixes = [
        'alertasSeguridad',
        'eventosSeguridad',
        'sirenas',
        'alarmas',
        'vehiculos',
        'colectivos',
        'trackers',
        'activos',
        'luminarias',
        'gateways',
        'paradas',
        'puntos',
        'emergencias',
        'hospitales',
        'ambulancias',
        'camiones',
        'centros',
        'zonas-seguridad',
      ];
      const anchor = allLayers.find((l) =>
        entityPrefixes.some((p) => l.id.startsWith(p)),
      );
      if (anchor) {
        this.map.moveLayer(this.fillLayerId, anchor.id);
        this.map.moveLayer(this.outlineLayerId, anchor.id);
      }
    } catch {
      // ignore
    }
  }

  updateLocalidades(localidades: ILocalidad[]): void {
    if (!this.map || !this.isInitialized) return;

    const features: GeoJSON.Feature<GeoJSON.Polygon>[] = [];

    for (const localidad of localidades) {
      const coords = (localidad as any).geojson?.coordinates;
      if (!coords || !Array.isArray(coords) || coords.length === 0) continue;

      features.push({
        type: 'Feature',
        id: localidad._id || `localidad-${features.length}`,
        geometry: {
          type: 'Polygon',
          coordinates: coords as any,
        },
        properties: {
          id: localidad._id,
          nombre: localidad.nombre,
        },
      });
    }

    this.applyFeatures(features);
  }

  private applyFeatures(features: GeoJSON.Feature<GeoJSON.Polygon>[]): void {
    if (!this.map) return;
    const source = (() => {
      try {
        return this.map.getSource(this.sourceId) as mapboxgl.GeoJSONSource;
      } catch {
        return null;
      }
    })();

    if (source) {
      source.setData({ type: 'FeatureCollection', features });
      this.pendingFeatures = null;
    } else {
      this.pendingFeatures = features;
      this.map.once('idle', () => {
        if (this.pendingFeatures) {
          this.applyFeatures(this.pendingFeatures);
        }
      });
    }
  }

  clear(): void {
    if (!this.map || !this.isInitialized) return;
    if (!this.map.isStyleLoaded()) return;
    try {
      const source = this.map.getSource(
        this.sourceId,
      ) as mapboxgl.GeoJSONSource;
      source?.setData({ type: 'FeatureCollection', features: [] });
    } catch {
      // ignore
    }
  }

  setVisibility(visible: boolean): void {
    if (!this.map) return;
    const visibility = visible ? 'visible' : 'none';
    try {
      if (this.map.getLayer(this.fillLayerId)) {
        this.map.setLayoutProperty(this.fillLayerId, 'visibility', visibility);
      }
      if (this.map.getLayer(this.outlineLayerId)) {
        this.map.setLayoutProperty(
          this.outlineLayerId,
          'visibility',
          visibility,
        );
      }
    } catch {
      // ignore
    }
  }
}
